import { useEffect, useRef, useState } from "react";

const STT_WS_URL =
  process.env.REACT_APP_STT_WS_URL ||
  "wss://stt.omlilabs.ai/real-time?vad_enabled=true";
const TARGET_SAMPLE_RATE = 16000;
const CHUNK_SECONDS = 29;

// Decode the audio at `url`, resample to mono 16 kHz, and return Int16 PCM.
async function fetchAndDecodePCM(url, signal) {
  // Token is already in the URL as ?token=...; don't send credentials,
  // because backend CORS uses allow_origins=['*'] which is incompatible
  // with credentialed requests per the CORS spec.
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`audio fetch failed: ${res.status}`);
  const arrayBuf = await res.arrayBuffer();

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const decodeCtx = new AudioCtx();
  let decoded;
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuf.slice(0));
  } finally {
    decodeCtx.close && decodeCtx.close();
  }

  const targetLength = Math.ceil(decoded.duration * TARGET_SAMPLE_RATE);
  const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const offline = new OfflineCtx(1, targetLength, TARGET_SAMPLE_RATE);
  const srcNode = offline.createBufferSource();
  srcNode.buffer = decoded;

  if (decoded.numberOfChannels > 1) {
    const merger = offline.createChannelMerger(1);
    const splitter = offline.createChannelSplitter(decoded.numberOfChannels);
    srcNode.connect(splitter);
    const gain = offline.createGain();
    gain.gain.value = 1 / decoded.numberOfChannels;
    for (let i = 0; i < decoded.numberOfChannels; i += 1) {
      splitter.connect(gain, i);
    }
    gain.connect(merger, 0, 0);
    merger.connect(offline.destination);
  } else {
    srcNode.connect(offline.destination);
  }

  srcNode.start(0);
  const rendered = await offline.startRendering();
  const float = rendered.getChannelData(0);

  const int16 = new Int16Array(float.length);
  for (let i = 0; i < float.length; i += 1) {
    const s = Math.max(-1, Math.min(1, float[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
}

function sliceIntoChunks(int16, samplesPerChunk) {
  const chunks = [];
  for (let off = 0; off < int16.length; off += samplesPerChunk) {
    chunks.push(int16.subarray(off, Math.min(off + samplesPerChunk, int16.length)));
  }
  return chunks;
}

// Standalone transcription: fetch+decode the clip, stream 29s int16 chunks
// to the STT WS, accumulate the JSON `text` messages, resolve with the full
// transcript. `onPartial(text)` fires after each chunk's transcript lands.
// Abort via AbortController on the `signal`.
export async function transcribeAudioOmli(audioUrl, { signal, onPartial } = {}) {
  if (!audioUrl) return "";
  return new Promise((resolve, reject) => {
    let finalText = "";
    let chunksRemaining = 0;
    let allSent = false;
    let settled = false;

    const settle = (fn, val) => {
      if (settled) return;
      settled = true;
      try { ws.close(); } catch {}
      fn(val);
    };

    const ws = new WebSocket(STT_WS_URL);
    ws.binaryType = "arraybuffer";

    const onAbort = () => settle(reject, new DOMException("Aborted", "AbortError"));
    if (signal) {
      if (signal.aborted) return settle(reject, new DOMException("Aborted", "AbortError"));
      signal.addEventListener("abort", onAbort, { once: true });
    }

    const maybeFinish = () => {
      if (allSent && chunksRemaining <= 0) settle(resolve, finalText);
    };

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(typeof ev.data === "string" ? ev.data : "");
        const text = (data && data.text ? String(data.text) : "").trim();
        if (text) {
          finalText = finalText ? `${finalText} ${text}` : text;
          if (onPartial) onPartial(finalText);
        }
      } catch {
        // ignore non-JSON frames (keepalives etc.)
      }
      chunksRemaining -= 1;
      maybeFinish();
    };

    ws.onerror = () => settle(reject, new Error("STT connection error"));
    ws.onclose = () => {
      // If server closed before we settled, resolve with whatever we got.
      if (!settled) {
        settled = true;
        if (signal) signal.removeEventListener("abort", onAbort);
        resolve(finalText);
      }
    };

    ws.onopen = async () => {
      try {
        const pcm = await fetchAndDecodePCM(audioUrl, signal);
        if (settled) return;
        const samplesPerChunk = TARGET_SAMPLE_RATE * CHUNK_SECONDS;
        const chunks = sliceIntoChunks(pcm, samplesPerChunk);
        chunksRemaining = chunks.length || 1;
        for (const chunk of chunks) {
          if (settled || ws.readyState !== WebSocket.OPEN) break;
          ws.send(chunk.slice().buffer);
        }
        allSent = true;
        if (chunks.length === 0) settle(resolve, "");
      } catch (e) {
        if (e.name === "AbortError") return settle(reject, e);
        settle(reject, e);
      }
    };
  });
}

// Hook variant — kept for backward compat (used by the original Dashboard).
// Dashboard.v2 calls `transcribeAudioOmli` directly so prefetch can populate
// a shared cache across all tasks.
export function useLiveSTT(audioUrl) {
  const [transcript, setTranscript] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!audioUrl) {
      setTranscript("");
      setStatus("idle");
      setError(null);
      return undefined;
    }
    const abort = new AbortController();
    abortRef.current = abort;
    setTranscript("");
    setError(null);
    setStatus("loading");

    let cancelled = false;
    (async () => {
      try {
        const text = await transcribeAudioOmli(audioUrl, {
          signal: abort.signal,
          onPartial: (partial) => {
            if (cancelled) return;
            setTranscript(partial);
            setStatus("streaming");
          },
        });
        if (cancelled) return;
        setTranscript(text);
        setStatus("done");
      } catch (e) {
        if (cancelled || e.name === "AbortError") return;
        setError(e.message || "Failed");
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
      abortRef.current = null;
    };
  }, [audioUrl]);

  return { transcript, status, error };
}
